import { useCallback, useRef, useState } from 'react';

import { changeCardApply } from '../../services/changeCard';
import { needsAttestation } from '../../services/mfa';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';

/**
 * The Change card move, made from the review step (KPR-709; keepr-api
 * SCHEMA.md § Change card, "The move").
 *
 * `apply(body, token)` sends the dry run's body as it was sent, plus its
 * confirm token. A card that asks for a signature on every change answers
 * 428: `attestation` is then the prompt to draw (`{ mode, cardId }`), and
 * `sign(token)` repeats the move with the signature, `cancelSign()` drops it.
 *
 * - `result` is the move's answer once it landed: 200 (`applied: true`, a
 *   change made in the request) or 202 (`applied: false` with its `task`).
 * - `refusal` is any other answer, as `{ code, message }`; nothing changed.
 *
 * `apply` and `sign` resolve `{ data }`, `{ refusal }` or `{ signing }`.
 */
export default function useChangeCardApply(collectionId, { signingCardId = null } = {}) {
	const [busy, setBusy] = useState(false);
	const [result, setResult] = useState(null);
	const [refusal, setRefusal] = useState(null);
	const [attestation, setAttestation] = useState(null);
	const pending = useRef(null);

	const send = useCallback(async (body, token, signature) => {
		setBusy(true);
		setRefusal(null);
		try {
			const data = await changeCardApply(collectionId, body, token, { attestation: signature });
			setAttestation(null);
			pending.current = null;
			setResult(data);
			return { data };
		} catch (err) {
			if (needsAttestation(err)) {
				pending.current = { body, token };
				setAttestation({ mode: err?.response?.data?.mode || 'pin', cardId: signingCardId });
				return { signing: true };
			}
			setAttestation(null);
			pending.current = null;
			const refused = { code: apiErrorCode(err), message: apiErrorMessage(err, '') };
			setRefusal(refused);
			return { refusal: refused };
		} finally {
			setBusy(false);
		}
	}, [collectionId, signingCardId]);

	const apply = useCallback((body, token) => send(body, token, null), [send]);

	const sign = useCallback(signature => {
		const held = pending.current;
		if (!held) return null;
		return send(held.body, held.token, signature);
	}, [send]);

	const cancelSign = useCallback(() => {
		pending.current = null;
		setAttestation(null);
	}, []);

	const clearRefusal = useCallback(() => setRefusal(null), []);

	return { apply, sign, cancelSign, busy, result, refusal, clearRefusal, attestation };
}
